import { object, string } from "yup";
import { useFormik } from "formik";
import { useModalHooks } from "../../../../../../App/State/Hooks/useModalHooks";
import { useNotification } from "../../../../../../App/State/Hooks/useNotificationHooks";
import { useCreateAbacAppAttributeMutation } from "../../../../AbacAppQueries";
import { useCallback } from "react";
import { ICreateAttribute } from "../../../../../../App/Typings/AbacAttribute";

export default function useCreateAttributeFormState(
  appId: string,
  featureId?: string
) {
  const createAttributeFormTitle = "Create New Attribute";
  const createAttributeSuccessMessage = "New Attribute created!";
  const createAttributeErrorMessage = "Error creating new Attribute.";
  const { closeModal } = useModalHooks();
  const { displaySuccess, displayError } = useNotification();
  const { mutate: createNewAttribute, isLoading: createIsLoading } =
    useCreateAbacAppAttributeMutation(appId, featureId);

  //Validation for the create attribute form
  const validationSchema = object().shape({
    attributeName: string()
      .required("Attribute Name is required")
      .trim()
      .max(100, "Attribute name must be less than 100 characters"),
    attributeDescription: string()
      .trim()
      .max(500, "Attribute description must be less than 500 characters"),
  });

  //Initial create attribute formik setup
  const formik = useFormik({
    initialValues: {
      attributeName: "",
      attributeDescription: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      let newAttribute: ICreateAttribute = {
        appId: appId,
        featureId: featureId,
        name: values.attributeName.trim(),
        description: values.attributeDescription.trim(),
        allowCustomAssign: false,
        isDefault: false,
      };
      createAttribute(newAttribute);
    },
  });

  //Initial create app formik setup
  const handleCreateModalClose = () => {
    if (!createIsLoading) {
      closeModal();
      formik.resetForm();
    } else return;
  };

  // Create a new Attribute application
  const createAttribute = useCallback(
    function submitAttribute(newAttribute: ICreateAttribute) {
      if (!createIsLoading) {
        createNewAttribute(newAttribute, {
          onError: () => {
            displayError(createAttributeErrorMessage);
          },
          onSuccess: () => {
            displaySuccess(createAttributeSuccessMessage);
            closeModal();
            formik.resetForm();
          },
        });
      }
    },
    [
      createNewAttribute,
      createIsLoading,
      displayError,
      displaySuccess,
      closeModal,
      formik,
    ]
  );

  return {
    getters: {
      createAttributeFormTitle,
      createIsLoading,
      formik,
    },
    handlers: { handleCreateModalClose },
  };
}
